/**
 * EngineDetailIntent Component
 *
 * Engine-specific detail view for Intent Engine.
 * Displays intent coverage score breakdown, covered/gap intents tables,
 * audience segments, competitor intent matrix, and store text recommendations.
 */

import { useState, useMemo } from "react";
import { Subheading, Text } from "@a7y/tailwind-catalyst";
import { Badge } from "@a7y/tailwind-catalyst";
import type {
  DataQualityFlag,
  IntentEngineSpecificAnalysis,
  IntentItem,
  IntentAudienceSegment,
  IntentSource,
  IntentStoreTextRecommendation,
  UnifiedDetailedAnalysis,
} from "@a7y/types";
import { isDataQualityFlag } from "@a7y/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../tooltip";
import { CollapsibleSection } from "./CollapsibleSection";
import { ScoreGauge } from "./ScoreGauge";
import { CircularGauge } from "./CircularGauge";
import { HorizontalBar } from "./HorizontalBar";
import { DevCardBadge } from "./DevCardBadge";

export interface EngineDetailIntentProps {
  /** Intent-specific analysis data */
  analysis: IntentEngineSpecificAnalysis;
  /** Detailed analysis with score_breakdown */
  detail: UnifiedDetailedAnalysis;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TH_CLASS =
  "px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider";

const CATEGORY_COLOR: Record<IntentItem["intent_category"], "violet" | "rose" | "sky"> = {
  functional: "violet",
  emotional: "rose",
  social: "sky",
};

const STRENGTH_COLOR: Record<IntentItem["articulation_strength"], "green" | "amber" | "red"> = {
  strong: "green",
  weak: "amber",
  absent: "red",
};

const SOURCE_COLOR: Record<IntentSource, "blue" | "indigo" | "purple" | "zinc" | "teal"> = {
  listing: "blue",
  reviews: "indigo",
  competitors: "purple",
  inferred: "zinc",
  // A7Y-4350 — intents clustered and named from harvested search demand.
  observed: "teal",
};

const IMPACT_COLOR: Record<IntentStoreTextRecommendation["estimated_impact"], string> = {
  high: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  low: "bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-300",
};

const FIELD_LABEL: Record<IntentStoreTextRecommendation["target_field"], string> = {
  title: "Title",
  subtitle: "Subtitle",
  description: "Description",
  whats_new: "What's New",
  promotional_text: "Promo Text",
};

// ---------------------------------------------------------------------------
// Data-quality flag rendering (A7Y-1084 — composes with A7Y-1083)
// ---------------------------------------------------------------------------
//
// `@a7y/ui` intentionally does NOT depend on the webapp's `DataQualityFlagBadge`
// (see the note on `EngineDetailAiDiscovery.tsx`). When the audit pipeline's
// plausibility gate (A7Y-1083) rewrites an element of
// `recommended_store_text_additions` into a `DataQualityFlag` sentinel — e.g.
// because the intent-engine business-model guard quarantined a "no paywalls"
// suggestion for a subscription app (A7Y-1084) — we render an inline
// "Unavailable" badge in place of the recommendation card using local
// `@a7y/ui` primitives. This matches the webapp `DataQualityFlagBadge` visual
// without crossing the package boundary.

type FlagBadgeColor = "red" | "amber" | "zinc";

function flagBadgeColor(severity: string): FlagBadgeColor {
  switch (severity) {
    case "fabricated":
    case "impossible":
      return "red";
    case "contradicts":
      return "amber";
    default:
      return "zinc";
  }
}

function StoreTextRecommendationFlagBadge({
  flag,
}: {
  flag: DataQualityFlag["__dataQualityFlag"];
}) {
  const color = flagBadgeColor(flag.severity);
  const tooltip = `Unavailable — data quality check failed: ${flag.reason}`;
  return (
    <div
      className="border border-dashed border-gray-200 dark:border-zinc-700 rounded-lg p-4"
      data-data-quality-flag={flag.severity}
      data-testid="intent-store-text-rec-data-quality-flag"
    >
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Badge color={color} className="text-xs">
                Unavailable
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
        <Text className="text-sm text-gray-500 dark:text-gray-400 italic">
          Recommendation withheld
        </Text>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Renders the intent table used by both covered and gap sections. */
function IntentTable({
  intents,
  showRecommendedLanguage,
}: {
  intents: IntentItem[];
  showRecommendedLanguage: boolean;
}) {
  if (intents.length === 0) {
    return (
      <Text className="text-sm text-gray-500 dark:text-gray-400 py-2">
        No intents in this category.
      </Text>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
        <thead className="bg-gray-50 dark:bg-zinc-800">
          <tr>
            <th className={`${TH_CLASS} text-left`}>Intent</th>
            <th className={`${TH_CLASS} text-left w-24`}>Category</th>
            <th className={`${TH_CLASS} text-left w-28`}>Audience</th>
            <th className={`${TH_CLASS} text-center w-24`}>Strength</th>
            <th className={`${TH_CLASS} text-right w-28`}>LLM Relevance</th>
            <th className={`${TH_CLASS} text-left w-20`}>Source</th>
            {showRecommendedLanguage && (
              <th className={`${TH_CLASS} text-left`}>Recommended Language</th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
          {intents.map((intent) => (
            <tr key={intent.intent_id} className="hover:bg-gray-50 dark:hover:bg-zinc-800">
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                {intent.intent_statement}
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                <Badge color={CATEGORY_COLOR[intent.intent_category]}>
                  {intent.intent_category}
                </Badge>
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                {intent.audience_segment}
              </td>
              <td className="px-4 py-2 text-center whitespace-nowrap">
                <Badge color={STRENGTH_COLOR[intent.articulation_strength]}>
                  {intent.articulation_strength}
                </Badge>
              </td>
              <td className="px-4 py-2">
                <HorizontalBar
                  score={Math.round(intent.llm_relevance * 100)}
                  label=""
                  showValue
                />
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                <Badge color={SOURCE_COLOR[intent.source]}>{intent.source}</Badge>
              </td>
              {showRecommendedLanguage && (
                <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                  <span className="line-clamp-2">{intent.recommended_language}</span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function EngineDetailIntent({
  analysis,
  detail,
  printMode,
  className = "",
}: EngineDetailIntentProps) {
  const [coverageScoreOpen, setCoverageScoreOpen] = useState(true);
  const [coveredOpen, setCoveredOpen] = useState(true);
  const [gapOpen, setGapOpen] = useState(true);
  const [audienceOpen, setAudienceOpen] = useState(true);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [recsOpen, setRecsOpen] = useState(true);

  const sb = detail.score_breakdown;

  // Extract the 4 score dimensions from detail.score_breakdown
  const dimensions = useMemo(
    () => [
      { key: "breadth", label: "Breadth", score: sb?.breadth?.score ?? 0, explanation: sb?.breadth?.explanation ?? "" },
      { key: "depth", label: "Depth", score: sb?.depth?.score ?? 0, explanation: sb?.depth?.explanation ?? "" },
      { key: "gap_impact", label: "Gap Impact", score: sb?.gap_impact?.score ?? 0, explanation: sb?.gap_impact?.explanation ?? "" },
      { key: "differentiation", label: "Differentiation", score: sb?.differentiation?.score ?? 0, explanation: sb?.differentiation?.explanation ?? "" },
    ],
    [sb],
  );

  const {
    covered_intents,
    gap_intents,
    audience_segments,
    competitor_intent_matrix,
    recommended_store_text_additions,
  } = analysis;

  return (
    <DevCardBadge name="EngineDetailIntent">
      <div className={`space-y-6 ${className}`}>
        {/* Intent Coverage Score */}
        <CollapsibleSection
          title="Intent Coverage Score"
          isExpanded={coverageScoreOpen}
          onToggle={() => setCoverageScoreOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Overall gauge */}
            <div className="flex flex-col items-center gap-1">
              <ScoreGauge score={analysis.intent_coverage_score} size="large" />
              <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Overall Coverage
              </Text>
            </div>

            {/* 4-dimension grid */}
            <div className="flex-1 w-full grid grid-cols-2 gap-3">
              {dimensions.map((dim) => (
                <div
                  key={dim.key}
                  className="bg-gray-50 dark:bg-zinc-800/50 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {dim.label}
                    </span>
                    <span className="text-lg font-bold font-accent text-zinc-900 dark:text-white">
                      {dim.score}
                    </span>
                  </div>
                  <HorizontalBar score={dim.score} label="" showValue={false} />
                  {dim.explanation && (
                    <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {dim.explanation}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* Covered Intents */}
        {covered_intents.length > 0 && (
          <CollapsibleSection
            title={`Covered Intents (${covered_intents.length})`}
            isExpanded={coveredOpen}
            onToggle={() => setCoveredOpen((v) => !v)}
            headerColor="green"
            forceExpanded={printMode}
          >
            <IntentTable intents={covered_intents} showRecommendedLanguage={false} />
          </CollapsibleSection>
        )}

        {/* Gap Intents */}
        {gap_intents.length > 0 && (
          <CollapsibleSection
            title={`Intent Gaps (${gap_intents.length})`}
            isExpanded={gapOpen}
            onToggle={() => setGapOpen((v) => !v)}
            headerColor="yellow"
            forceExpanded={printMode}
          >
            <IntentTable intents={gap_intents} showRecommendedLanguage />
          </CollapsibleSection>
        )}

        {/* Audience Segments */}
        {audience_segments.length > 0 && (
          <CollapsibleSection
            title={`Audience Segments (${audience_segments.length})`}
            isExpanded={audienceOpen}
            onToggle={() => setAudienceOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {audience_segments.map((seg: IntentAudienceSegment) => (
                <div
                  key={seg.name}
                  className="border border-gray-200 dark:border-zinc-700 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <Subheading level={5} className="truncate">
                      {seg.name}
                    </Subheading>
                    {seg.explicitly_targeted && (
                      <Badge color="green">Targeted</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <CircularGauge score={seg.coverage_score} size="sm" />
                    <div>
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        {seg.intent_count} intent{seg.intent_count !== 1 ? "s" : ""}
                      </Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Competitor Intent Matrix */}
        {competitor_intent_matrix.rows.length > 0 && (
          <CollapsibleSection
            title={`Competitor Intent Matrix (${competitor_intent_matrix.rows.length} intents)`}
            isExpanded={matrixOpen}
            onToggle={() => setMatrixOpen((v) => !v)}
            headerColor="gray"
            forceExpanded={printMode}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                <thead className="bg-gray-50 dark:bg-zinc-800">
                  <tr>
                    <th className={`${TH_CLASS} text-left`}>Intent</th>
                    <th className={`${TH_CLASS} text-center w-24`}>Your App</th>
                    {competitor_intent_matrix.competitor_names.map((name, idx) => (
                      <th
                        key={competitor_intent_matrix.competitor_ids[idx]}
                        className={`${TH_CLASS} text-center w-24`}
                      >
                        <span className="truncate block max-w-[96px]">{name}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-700">
                  {competitor_intent_matrix.rows.map((row) => (
                    <tr key={row.intent_id} className="hover:bg-gray-50 dark:hover:bg-zinc-800">
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                        {row.intent_statement}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {row.target_app_covers ? (
                          <span className="text-green-500 font-bold">&#10003;</span>
                        ) : (
                          <span className="text-gray-300 dark:text-zinc-600">&mdash;</span>
                        )}
                      </td>
                      {competitor_intent_matrix.competitor_ids.map((compId) => (
                        <td key={compId} className="px-4 py-2 text-center">
                          {row.competitor_coverage[compId] ? (
                            <span className="text-green-500 font-bold">&#10003;</span>
                          ) : (
                            <span className="text-gray-300 dark:text-zinc-600">&mdash;</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* Store Text Recommendations */}
        {recommended_store_text_additions.length > 0 && (
          <CollapsibleSection
            title={`Store Text Recommendations (${recommended_store_text_additions.length})`}
            isExpanded={recsOpen}
            onToggle={() => setRecsOpen((v) => !v)}
            headerColor="blue"
            forceExpanded={printMode}
          >
            <div className="space-y-3">
              {(
                recommended_store_text_additions as Array<
                  IntentStoreTextRecommendation | DataQualityFlag
                >
              ).map((rec, idx) => {
                  // A7Y-1084: when the plausibility gate quarantines a
                  // specific recommendation (e.g. "no paywalls" copy on a
                  // subscription app), the array element is rewritten to a
                  // `DataQualityFlag` sentinel. Render the placeholder badge
                  // in place of the recommendation card.
                  if (isDataQualityFlag(rec)) {
                    return (
                      <StoreTextRecommendationFlagBadge
                        key={`data-quality-flag-${idx}`}
                        flag={rec.__dataQualityFlag}
                      />
                    );
                  }
                  return (
                    <div
                      key={rec.intent_id}
                      className="border border-gray-200 dark:border-zinc-700 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge color="indigo">{FIELD_LABEL[rec.target_field]}</Badge>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${IMPACT_COLOR[rec.estimated_impact]}`}
                        >
                          {rec.estimated_impact.charAt(0).toUpperCase() + rec.estimated_impact.slice(1)} Impact
                        </span>
                      </div>
                      <div className="bg-gray-50 dark:bg-zinc-800/50 rounded-md p-3 mb-2">
                        <Text className="text-sm text-gray-900 dark:text-white italic">
                          &ldquo;{rec.suggested_text}&rdquo;
                        </Text>
                      </div>
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        {rec.rationale}
                      </Text>
                    </div>
                  );
                },
              )}
            </div>
          </CollapsibleSection>
        )}
      </div>
    </DevCardBadge>
  );
}
